import { Block } from "app/shared/components/blocks/block";
import DotContainer from "app/shared/components/dots";
import { CARDITEM } from "app/shared/services/services-model.type";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import * as React from "react";
import { StyleSheet } from "react-native";
import { IconButton, useTheme } from "react-native-paper";
import { Extrapolate, interpolate, useAnimatedStyle, useSharedValue } from "react-native-reanimated";
import Carousel, { ICarouselInstance } from "react-native-reanimated-carousel";
import { CarouselRenderItemInfo } from "react-native-reanimated-carousel/lib/typescript/types";
import { ParamCardRequest, SWITCH_ACTION } from "../list-card.web";
import WrappingCard from "./wrapping-card";

export interface CardSwipeProps {
  listCards?: CARDITEM[];
  selectIndex: number;
  onChangeIndex: Function;
  requestDetail: Function;
}

const CardSwipe = (props: CardSwipeProps) => {
  const { colors } = useTheme();
  const isDesktop = useDesktopMediaQuery();

  const { listCards = [], selectIndex, onChangeIndex, requestDetail } = props;
  const progressValue = useSharedValue<number>(selectIndex);
  const r = React.useRef<ICarouselInstance>(null);
  const isSnap = React.useRef(false);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        carouselStyle: {
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          height: 220,
          aspectRatio: 350 / 220,
        },
        button: { display: isDesktop ? "flex" : "none" },
      }),
    [isDesktop],
  );

  const prevPage = React.useCallback(() => {
    r.current?.prev();
  }, []);

  const nextPage = React.useCallback(() => {
    r.current?.next();
  }, []);

  const renderItem = ({ item, index, animationValue }: CarouselRenderItemInfo<any>) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const _anim: any = useAnimatedStyle(() => {
      const translateX = interpolate(animationValue.value, [-1, 0, 1], [120, 0, -120], Extrapolate.EXTEND);
      const scale = interpolate(animationValue.value, [-1, 0, 1], [0.9, 1, 0.9], Extrapolate.EXTEND);
      const opacity = interpolate(animationValue.value, [-1, 0, 1], [0.25, 1, 0.25], Extrapolate.EXTEND);
      const rotateY = `${interpolate(animationValue.value, [-1, 0, 1], [-35, 0, 35], Extrapolate.EXTEND)}deg`;
      return {
        opacity,
        transform: [{ translateX }, { perspective: 1200 }, { rotateY }, { scale }],
      };
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const onPress = React.useCallback(() => {
      if (r.current?.getCurrentIndex() === index && isSnap.current === false) {
        const param: ParamCardRequest = {
          cardID: item?.cardId,
        };
        requestDetail(SWITCH_ACTION.GET_DETAIL, param);
      }
      if (r.current?.getCurrentIndex() !== index && isSnap.current === false) {
        r.current?.scrollTo({
          animated: true,
          count: animationValue.value,
        });
      }
    }, [animationValue.value, index, item]);
    return <WrappingCard style={_anim} widthCard={350} card={item} onPress={onPress} />;
  };

  return (
    <>
      <Carousel
        ref={r}
        mode="parallax"
        style={styles.carouselStyle}
        width={350}
        autoPlay={false}
        loop={false}
        modeConfig={{
          parallaxScrollingOffset: 0,
          parallaxScrollingScale: 1,
        }}
        panGestureHandlerProps={{
          activeOffsetX: [-10, 10],
        }}
        defaultIndex={selectIndex}
        scrollAnimationDuration={50}
        snapEnabled={false}
        onScrollBegin={() => {
          isSnap.current = true;
        }}
        onScrollEnd={() => {
          isSnap.current = false;
        }}
        onSnapToItem={(_index) => {
          onChangeIndex(_index);
        }}
        onProgressChange={(_, absoluteProgress) => (progressValue.value = absoluteProgress)}
        data={listCards}
        renderItem={renderItem}
      />
      <Block row middle center>
        <IconButton
          style={styles.button}
          icon="chevron-left"
          color={selectIndex === 0 ? colors.disabled : colors.accent}
          size={24}
          onPress={prevPage}
        />
        <DotContainer activeDotColor={colors.accent} sizeRatio={1} curPage={selectIndex} maxPage={listCards.length} />
        <IconButton
          style={styles.button}
          icon="chevron-right"
          color={selectIndex === listCards.length - 1 ? colors.disabled : colors.accent}
          size={24}
          onPress={nextPage}
        />
      </Block>
    </>
  );
};

export default CardSwipe;
